import { useEffect, useRef, useState } from 'react';
import { useClickAway } from '@optimus-web/helpers';
import { OptimusTextKey } from '@optimus-web/localization';
import { ExpandIcon } from '../ExpandIcon';
import { ProgressIndicator, ProgressIndicatorProps } from './ProgressIndicator';
import { OpenableVerticalStepperContainer, StepCountElement, ToggleButtonElement } from './style';

export const OpenableVerticalProgressIndicator = ({
    steps,
    activeStep,
    onStepChange,
    ...other
}: ProgressIndicatorProps) => {
    const [isOpened, setIsOpened] = useState(false);
    const { registerClickAway, deregisterClickAway } = useClickAway();

    const containerRef = useRef(null);

    useEffect(() => {
        if (isOpened) {
            registerClickAway({
                handler: handleClose,
                roots: [() => containerRef.current],
            });
        } else {
            deregisterClickAway();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpened]);

    const handleClose = () => {
        setIsOpened(false);
    };

    const handleOpen = () => {
        setIsOpened(true);
    };

    const handleStepChange = (value: string) => {
        setIsOpened(false);

        if (onStepChange) {
            onStepChange(value);
        }
    };

    const activeItemIndex = steps.findIndex((item) => item.value === activeStep);
    const newSteps = isOpened
        ? steps
        : steps.map((item, index) => ({ ...item, order: index })).filter((item) => item.value === activeStep);

    return (
        <OpenableVerticalStepperContainer ref={containerRef}>
            <ToggleButtonElement
                type="button"
                onClick={isOpened ? handleClose : handleOpen}
                small={steps[activeItemIndex] ? !steps[activeItemIndex]?.optional : true}
                aria-expanded={isOpened}
            >
                {!isOpened ? (
                    <StepCountElement>
                        {activeItemIndex + 1} {OptimusTextKey.Of.toLowerCase()} {steps.length}
                    </StepCountElement>
                ) : null}
                <ExpandIcon expanded={isOpened} />
            </ToggleButtonElement>
            <ProgressIndicator
                {...other}
                activeStep={activeStep}
                vertical
                isOpened={isOpened}
                steps={newSteps}
                onStepChange={handleStepChange}
                small
                isOpenable
            />
        </OpenableVerticalStepperContainer>
    );
};
